/**
 * Elliott conversational-lesson rollout flag.
 *
 * The new four-step Elliott lesson experience (Content → Case Studies →
 * Live Simulation → Quiz) replaces the classic lesson page per-org while the
 * content backfill (scripts/elliottBackfillSections.js on the BE) is rolled
 * out org by org. Orgs NOT in the allowlist keep the classic MenteeTask page.
 *
 * CUTOVER: once every org's backfill has run, flip
 * `ELLIOTT_LESSON_ALL_ORGS = true` (the allowlist then becomes irrelevant).
 */

/** Set to true to enable the Elliott lesson experience for EVERY org. */
export const ELLIOTT_LESSON_ALL_ORGS = true;

/**
 * Orgs with the Elliott experience enabled (backfill already run).
 * Both the Mongo _id and the Auth0 authId are listed per org, since which one
 * is available depends on how the session was hydrated.
 * (Irrelevant while ELLIOTT_LESSON_ALL_ORGS is true — kept for staged rollback.)
 */
const ENABLED_ORG_IDS = new Set<string>([
  // The Playground (US)
  '6a182725f1b5d4a511343fb3',
  'org_GiHz1oRmIxSq03YX',
  // Nory AI (EU)
  '6a3d2684bd5070082868d6c5',
  'org_ibMIRpDEwdW19gDU'
]);

/**
 * Orgs explicitly OPTED OUT of the Elliott experience — they keep the classic
 * lesson page even when ELLIOTT_LESSON_ALL_ORGS is on. This blocklist wins over
 * everything.
 */
const DISABLED_ORG_IDS = new Set<string>([
  // Group Effort Initiative (US) — excluded from the Elliott rollout
  '6938931e8797d2e278fe1c38',
  'org_XkihyfFNYhtLPB1t'
]);

/**
 * Whether the Elliott lesson is enabled for the given org identifiers (_id / authId).
 * Explicit opt-out (DISABLED_ORG_IDS) takes precedence over the global flag and
 * the allowlist.
 */
export function isElliottLessonEnabled(...orgIds: Array<string | undefined | null>): boolean {
  if (orgIds.some((id) => Boolean(id && DISABLED_ORG_IDS.has(id)))) return false;
  if (ELLIOTT_LESSON_ALL_ORGS) return true;
  return orgIds.some((id) => Boolean(id && ENABLED_ORG_IDS.has(id)));
}
